import { type FormEvent, type Ref, useState } from 'react';
import classNames from 'classnames';

import { isAfter, isFuture, safeDifferenceInCalendarDays } from 'shared/util/datetime-fns';

import { trackSearchEdit } from '@/utils/tracking/finnAnalytics';

import type { AutocompleteDestination, MapBounds, Orientation, RoomWithPeople, SearchConfig } from '@/interfaces';
import { TravellersPicker } from '@/containers/travellersPicker/TravellersPicker';
import { DestinationAutocomplete } from './containers/destinationAutocomplete/DestinationAutocomplete';
import { CheckInCheckOutCalendar } from '@/containers/checkInCheckOutCalendar/CheckInCheckOutCalendar';
import { SearchFormSubmit } from './components/searchFormSubmit/SearchFormSubmit';

export interface SearchFormProps {
    position: string;
    placeKey?: string;
    hierarchy?: string[];
    destinationFullName?: string;
    initialCheckInDate?: Date | number;
    initialCheckOutDate?: Date | number;
    initialRooms: readonly RoomWithPeople[];
    initialMapBounds?: MapBounds;
    mapOpen?: boolean;
    defaultErrorOnMissingDates?: boolean;
    searchConfig?: SearchConfig;
    orientation?: Orientation;
    checkinDateRef?: Ref<HTMLInputElement>;
    className?: string;
}

const validationErrorTexts = {
    missingDestination: 'Velg en gyldig destinasjon',
    onlyOneDate: 'Velg en gyldig dato for både innsjekk og utsjekk',
    invalidCheckInDate: 'Dato for innsjekk må være nåværende dato eller senere',
    invalidCheckOutDate: 'Dato for utsjekk må være etter dato for innsjekk',
    tooManyDaysBetweenDates: 'Det kan ikke være mer enn 30 dager mellom dato for inn- og utsjekk',
};

export const SearchForm = ({
    position,
    placeKey,
    hierarchy = [],
    destinationFullName,
    initialCheckInDate,
    initialCheckOutDate,
    initialRooms,
    initialMapBounds,
    mapOpen = false,
    defaultErrorOnMissingDates = false,
    searchConfig,
    orientation = 'vertical',
    checkinDateRef,
    className,
}: SearchFormProps) => {
    const [destination, setDestination] = useState<AutocompleteDestination | undefined>({
        text: destinationFullName,
        key: placeKey,
        searchable: true,
        hierarchy,
        type: 'place',
        themePath: !placeKey ? searchConfig?.urlPath : undefined,
    });
    const [checkInDate, setCheckInDate] = useState<Date | number | undefined>(initialCheckInDate);
    const [checkOutDate, setCheckOutDate] = useState<Date | number | undefined>(initialCheckOutDate);
    const [hotelRooms, setHotelRooms] = useState(initialRooms);
    const [ignoreMapBounds, setIgnoreMapBounds] = useState<boolean>(false);
    const [showErrors, setShowErrors] = useState(false);

    const validationErrors = {
        missingDestination: !destination ? validationErrorTexts.missingDestination : undefined,
        onlyOneDate: (checkInDate && !checkOutDate) || (!checkInDate && checkOutDate) ? validationErrorTexts.onlyOneDate : undefined,
        invalidCheckInDate: checkInDate && !isFuture(checkInDate) ? validationErrorTexts.invalidCheckInDate : undefined,
        invalidCheckOutDate:
            checkInDate && checkOutDate && isAfter(checkOutDate, checkInDate) ? validationErrorTexts.invalidCheckInDate : undefined,
        tooManyDaysBetweenDates:
            checkInDate && checkOutDate && safeDifferenceInCalendarDays(checkInDate, checkOutDate) > 30
                ? validationErrorTexts.tooManyDaysBetweenDates
                : undefined,
    };

    const calendarErrors = showErrors
        ? [
              validationErrors.onlyOneDate,
              validationErrors.invalidCheckInDate,
              validationErrors.invalidCheckOutDate,
              validationErrors.tooManyDaysBetweenDates,
          ].filter((error) => error)
        : [];

    const destinationErrors = showErrors ? [validationErrors.missingDestination].filter((error) => error) : [];

    const updateDestination = (dest?: AutocompleteDestination): void => {
        setIgnoreMapBounds(true);
        setDestination(dest);
    };
    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        if (Object.values(validationErrors).filter((error) => error).length > 0) {
            setShowErrors(true);
            event.preventDefault();
        } else {
            trackSearchEdit({
                position,
                country: destination ? destination.hierarchy[0] : undefined,
                destination: destination ? destination.hierarchy[1] : undefined,
                checkInDate,
                checkOutDate,
                rooms: hotelRooms,
                searchable: destination?.searchable,
                hotelKey: destination?.type === 'hotel' ? destination.key : undefined,
            });
        }
    };

    const useGrid = orientation === 'horizontal';

    return (
        <div
            data-testid="searchForm-container"
            className={classNames(className, {
                'grid grid--vertical-center grid--cols1upto768 grid--cols2upto990 grid--cols3from990': useGrid,
                'flex flex-wrap': !useGrid,
            })}>
            <div className={classNames('', { 'grid__unit u-pr8--gt768': useGrid, 'u-size1of1': !useGrid })}>
                <DestinationAutocomplete
                    key={`SearchFormAutocomplete-${destinationFullName}`}
                    defaultDestination={destinationFullName}
                    updateDestination={updateDestination}
                    errors={destinationErrors}
                />
            </div>
            <div className={classNames('', { 'grid__unit u-pl8--gt768 u-pr8--gt990': useGrid, 'search-form-calendar': !useGrid })}>
                <CheckInCheckOutCalendar
                    checkInDate={checkInDate}
                    checkOutDate={checkOutDate}
                    setCheckInDate={setCheckInDate}
                    setCheckOutDate={setCheckOutDate}
                    errors={calendarErrors}
                    isInvalid={defaultErrorOnMissingDates && !checkInDate && !checkOutDate}
                    checkinDateRef={checkinDateRef}
                />
            </div>
            <div className={classNames('', { 'grid__unit u-pr8--gt768 u-pl8--gt990': useGrid, 'search-form-rooms': !useGrid })}>
                <TravellersPicker rooms={hotelRooms} updateRooms={setHotelRooms} />
            </div>
            <div className={classNames('', { 'grid__unit u-pl8--gt768--lt990': useGrid, 'u-size1of1 search-form-submit': !useGrid })}>
                <SearchFormSubmit
                    destination={destination}
                    checkInDate={checkInDate}
                    checkOutDate={checkOutDate}
                    mapBounds={ignoreMapBounds ? undefined : initialMapBounds}
                    mapOpen={mapOpen}
                    rooms={hotelRooms}
                    handleSubmit={handleSubmit}
                    className={useGrid ? 'search-form-submit-horizontal' : undefined}
                />
            </div>
        </div>
    );
};
